import { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';

import { ToastContext } from '@/components/common/CommonToast';
import { useLoading } from '@/components/common/loader/LoadingContext';
import ButtonCustom from '@/components/common/ui/button';
import DialogCustom from '@/components/common/ui/dialog';
import { CustomDataTable } from '@/components/common/ui/table/CustomDataTable';
import { useTranslation } from '@/config/i18n';
import useFetchClient from '@/hooks/useFetchClient';
import usePagination from '@/hooks/usePagination';
import useSearch from '@/hooks/useSearch';
import { toastErrorMessage, toastMessage } from '@/lib/utils';
import { GiftColumns } from '@/pages/gift-management/table/GiftColumns';
import PATH from '@/routes/path';
import { apiRequest } from '@/services';
import { HTTP_STATUS } from '@/shared/constants';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/shared/constants/pagination';
import { Pagination } from '@/types/pagination';
import { giftStatus } from '@/shared/constants/gift';
import { GiftResponseType } from '@/types/gift';

type Props = {
  searchTrigger?: number;
}

const GiftList: React.FC<Props> = ({ searchTrigger }) => {
  const [t] = useTranslation('');
  const { apiService } = apiRequest();
  const { searchParams } = useSearch();
  const { toast } = useContext(ToastContext);
  const { query } = Object.fromEntries(searchParams.entries());
  const navigate = useNavigate();
  const { setLoadingGlobal } = useLoading();
  const [dialogChangeStatusVisible, setDialogChangeStatusVisible] =
    useState(false);
  const [dialogDeleteGiftVisible, setDialogDeleteGiftVisible] = useState(false);
  const [selectedGift, setSelectedGift] = useState<any>();

  const { data: gifts, error } = useFetchClient(
    async () => await apiService.gifts.query(query, params),
    [query, searchTrigger],
    {
      isLoadingGlobal: true,
      isScrollToTop: true,
    },
  );

  const params = useMemo(() => {
    return query
      ? undefined
      : { limit: DEFAULT_PER_PAGE, paginate: DEFAULT_PAGE };
  }, [query]);

  if (error) {
    toast?.current?.show(toastErrorMessage(t('common.toast.fail')));
  }

  const { paginationComputed, setPagination } = usePagination(
    gifts?.pagination,
  );

  function handleEditGift(id: number) {
    navigate(`${PATH.gift}/${PATH.gift_edit}/${id}`);
  }

  async function handleDeleteGift(rowData: GiftResponseType) {
    setSelectedGift({ rowData });
    const nextDialogVisibleValue = !dialogDeleteGiftVisible;
    setDialogDeleteGiftVisible(nextDialogVisibleValue);

    if (nextDialogVisibleValue) return;

    setLoadingGlobal(true);
    const { error: giftError } = await apiService.gifts.delete(rowData.id);
    setLoadingGlobal(false);

    if (giftError) {
      handleError(giftError);
      return;
    }

    const updatedGifts = gifts.data.filter((gift: any) => gift.id !== rowData.id);

    gifts.data = updatedGifts
    setPagination({
      ...paginationComputed,
      total_page: paginationComputed.total_page - 1,
    });
    if (!updatedGifts.length && paginationComputed.current_page && paginationComputed.current_page > 1) {
      const searchParams = new URLSearchParams(query);
      searchParams.delete('page');
      navigate({ search: searchParams.toString() });
    }
    toast?.current?.show(
      toastMessage(t('gift_list.toast.delete_gift_success')),
    );
  }

  const handleError = (error: any) => {
    const { status_code, errors } = error;
    if (status_code === HTTP_STATUS.FORM_ERROR) {
      const errorLimitStudent = errors?.find(
        (e: any) => e.error_code === 'MFS-E-LEARNING-ADMIN-USER-169',
      );
      if (errorLimitStudent) {
        toast?.current?.show(toastErrorMessage(errorLimitStudent?.message));
      }
    }
  };

  async function handleEditStatusGift(
    isActive: boolean,
    rowData: GiftResponseType,
  ) {
    setSelectedGift({ isActive, rowData });

    const {
      gift_types,
      status,
      id,
      points_required_for_student,
      points_required_for_teacher,
      email_content,
    } = rowData;

    if (
      status === giftStatus.DRAFT &&
      (isEmpty(gift_types) ||
        !points_required_for_student ||
        !points_required_for_teacher ||
        email_content === '')
    ) {
      toast?.current?.show(
        toastErrorMessage(t('gift_list.toast.gift_data_required')),
      );
      return;
    }

    const nextDialogVisibleValue = !dialogChangeStatusVisible;
    setDialogChangeStatusVisible(nextDialogVisibleValue);

    if (nextDialogVisibleValue) return;

    setLoadingGlobal(true);
    const { data: gift, error: giftError } = await apiService.gifts.update(id, {
      ...rowData,
      status: isActive ? giftStatus.PUBLIC : giftStatus.DRAFT,
      gift_type_id: gift_types.id,
    });
    setLoadingGlobal(false);
    if (giftError) {
      handleError(giftError);
      return;
    }

    const indexUpdate = gifts.data.findIndex((gift: any) => gift.id === id);
    if (indexUpdate !== -1) {
      gifts.data[indexUpdate].status = gift.data.status;
    }

    const msg =
      gift.data.status === giftStatus.PUBLIC
        ? t('gift_list.toast.public_gift_success')
        : t('gift_list.toast.save_draft_gift_success');
    toast?.current?.show(toastMessage(msg));
  }

  function onPaginationChanged(paginationState: Pagination) {
    setPagination({
      ...paginationComputed,
      paginate: paginationState.paginate,
      limit: paginationState.limit,
    });
  }

  function onSortChanged(paginationState: Pagination) {
    setPagination(paginationState);
  }

  return (
    <>
      <CustomDataTable
        wrapClassName="mt-[20px]"
        values={gifts?.data ?? []}
        columns={GiftColumns(
          t,
          handleEditGift,
          handleDeleteGift,
          handleEditStatusGift,
          paginationComputed,
        )}
        pagination={paginationComputed}
        onPaginationChanged={onPaginationChanged}
        onSortChanged={onSortChanged}
      />

      <DialogCustom
        header={
          selectedGift?.isActive
            ? t('gift_list.modal.change_status_public_to_draft.title')
            : t('gift_list.modal.change_status_draft_to_public.title')
        }
        visible={dialogChangeStatusVisible}
        headerClassName="border-b border-gray-200 !py-[13px]"
        contentClassName="border-b border-gray-200 !p-[16px] !text-[16px] !leading-[24px] text-center text-gray-900"
        onHide={() => setDialogChangeStatusVisible(false)}
        footer={
          <div className="flex justify-end gap-4">
            <ButtonCustom
              type="button"
              className="!rounded-md"
              severity="secondary"
              onClick={() => setDialogChangeStatusVisible(false)}
            >
              {selectedGift?.isActive
                ? t('gift_list.modal.change_status_public_to_draft.cancel')
                : t('gift_list.modal.change_status_draft_to_public.cancel')}
            </ButtonCustom>
            <ButtonCustom
              type="button"
              className="!rounded-md"
              onClick={() =>
                handleEditStatusGift(
                  selectedGift.isActive,
                  selectedGift.rowData,
                )
              }
            >
              {selectedGift?.isActive
                ? t('gift_list.modal.change_status_public_to_draft.yes')
                : t('gift_list.modal.change_status_draft_to_public.yes')}
            </ButtonCustom>
          </div>
        }
      >
        {!selectedGift?.isActive
          ? t('gift_list.modal.change_status_public_to_draft.content')
          : t('gift_list.modal.change_status_draft_to_public.content')}
      </DialogCustom>

      <DialogCustom
        header={t('gift_list.modal.delete.title')}
        visible={dialogDeleteGiftVisible}
        onHide={() => setDialogDeleteGiftVisible(false)}
        headerClassName="border-b border-gray-200 !py-[13px]"
        contentClassName="border-b border-gray-200 !p-[16px] !text-[16px] !leading-[24px] text-center text-gray-900"
        blockScroll
        footer={
          <div className="flex gap-4">
            <ButtonCustom
              type="button"
              className="w-full !rounded-full"
              severity="secondary"
              onClick={() => setDialogDeleteGiftVisible(false)}
            >
              {t('gift_list.modal.delete.cancel')}
            </ButtonCustom>
            <ButtonCustom
              type="button"
              className="w-full !rounded-full"
              onClick={() => handleDeleteGift(selectedGift?.rowData)}
            >
              {t('gift_list.modal.delete.delete')}
            </ButtonCustom>
          </div>
        }
      >
        {t('gift_list.modal.delete.content')}
      </DialogCustom>
    </>
  );
};

export default GiftList;
